/**
 * Custom Safari Builder Component
 * Allows clients to select multiple tours and create a custom safari itinerary
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { XMarkIcon, PlusIcon, TrashIcon, ShoppingCartIcon, CalendarIcon, CheckIcon, UserIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { Tour } from '../../types';
import { CustomSafariItinerary, SelectedTour } from '../../types/customSafari';
import { customSafariService } from '../../services/customSafariService';
import { tourService } from '../../services/tourService';
import { useTheme } from '../../contexts/ThemeContext';
import { useSafariBuilder } from '../../contexts/SafariBuilderContext';
import { userAuthService, User } from '../../services/userAuthService';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import TourCard from '../Tours/TourCard';
import ItineraryView from './ItineraryView';
import PricingBreakdown from './PricingBreakdown';
import AccommodationSelector from './AccommodationSelector';
import BookingModal from './BookingModal';
import UserAuthModal from './UserAuthModal';
import SchedulingModal from './SchedulingModal';
import { AccommodationSelection } from '../../types/accommodation';

interface CustomSafariBuilderProps {
  isOpen: boolean;
  onClose: () => void;
  initialTours?: Tour[];
}

const CustomSafariBuilder: React.FC<CustomSafariBuilderProps> = ({
  isOpen,
  onClose,
  initialTours = []
}) => {
  const { showSuccess, showError, showInfo, showWarning } = useToast();

  const { actualTheme } = useTheme();
  const { openCart, initialTours: contextTours, registerAddTourCallback } = useSafariBuilder();
  const effectiveInitialTours = initialTours.length > 0 ? initialTours : contextTours;
  const [selectedTours, setSelectedTours] = useState<Tour[]>(effectiveInitialTours);
  const [allTours, setAllTours] = useState<Tour[]>([]);
  const [itinerary, setItinerary] = useState<CustomSafariItinerary | null>(null);
  const [safariName, setSafariName] = useState('');
  const [activeTab, setActiveTab] = useState<'select' | 'itinerary' | 'accommodations'>('select');
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState<string>('USD'); // USD is default
  const { user: currentUser, isAuthenticated, refreshUser } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showSchedulingModal, setShowSchedulingModal] = useState(false);
  const [accommodationSelections, setAccommodationSelections] = useState<AccommodationSelection[]>([]);

  // Track processed initial tours to avoid re-processing
  const processedInitialToursRef = useRef<string>('');

  // Memoize addTour to prevent infinite loops - synchronous for instant response
  const addTour = useCallback((tour: Tour) => {
    // Immediate state update - no async operations
    setSelectedTours(prev => {
      if (!prev.find(t => t.id === tour.id)) {
        showSuccess('Tour Added', `${tour.title} has been added to your safari.`);
        return [...prev, tour];
      }
      return prev;
    });
  }, [showSuccess]); // No dependency on currentUser needed for browsing

  // Track if builder was just opened to process initial tours only once
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      prepareBuilder();
    } else {
      // Reset when closed
      wasOpenRef.current = false;
      setSelectedTours([]);
      setItinerary(null);
      setSafariName('');
      setSearchQuery('');
      processedInitialToursRef.current = ''; // Reset processed tours
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // Only depend on isOpen to prevent loops

  const prepareBuilder = async () => {
    // User is authenticated, proceed with loading
    loadTours();

    // Only process initial tours once when builder first opens
    if (!wasOpenRef.current) {
      wasOpenRef.current = true;
      const toursToUse = initialTours.length > 0 ? initialTours : contextTours;
      if (toursToUse.length > 0) {
        const toursKey = toursToUse.map(t => t.id).sort().join(',');
        processedInitialToursRef.current = toursKey;
        setSelectedTours(prev => {
          // Merge new tours with existing ones, avoiding duplicates
          const merged = [...prev];
          toursToUse.forEach(tour => {
            if (!merged.find(t => t.id === tour.id)) {
              merged.push(tour);
            }
          });
          return merged;
        });
      }
    }
  };

  const handleCreateDemo = async () => {
    setLoading(true);
    try {
      const demoLocations = [
        { name: 'JKIA Airport (Arrival)', coordinates: { lat: -1.332, lng: 36.927 } },
        { name: 'Lake Nakuru National Park', coordinates: { lat: -0.367, lng: 36.083 } },
        { name: 'Masai Mara Reserve', coordinates: { lat: -1.483, lng: 35.143 } },
        { name: 'Amboseli National Park', coordinates: { lat: -2.650, lng: 37.250 } },
        { name: 'Tsavo West National Park', coordinates: { lat: -2.983, lng: 38.167 } },
        { name: 'JKIA Airport (Departure)', coordinates: { lat: -1.332, lng: 36.927 } }
      ];

      const mockTours = demoLocations.map((loc, i) => ({
        id: `demo_tour_${i}`,
        title: i === 0 ? 'International Arrival & Transfer' : i === 5 ? 'Final Transfer & Departure' : `Safari Day ${i + 1}: ${loc.name}`,
        duration: '1 day',
        price: 250,
        currency: 'USD',
        location: {
          name: loc.name,
          country: 'Kenya',
          region: 'E. Africa',
          coordinates: loc.coordinates,
          address: ''
        },
        maxParticipants: 12,
        active: true,
        images: [],
        description: `A beautiful day at ${loc.name}.`,
        highlights: [],
        itinerary: [],
        includes: [],
        excludes: [],
        requirements: [],
        difficulty: 'Moderate',
        rating: 5,
        reviewCount: 100,
        category: 'Safari',
        guide: { id: 'demo_guide', firstName: 'John', lastName: 'Doe', rating: 5, specialties: ['Wildlife'], languages: ['English'], bio: 'Pro guide' },
        availability: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      } as any as Tour));

      setSelectedTours(mockTours);
      setSafariName('7-Day Dream Kenya Safari');
      setActiveTab('itinerary');
      showSuccess('Demo Safari Created', 'A 7-day Kenya circuit has been generated for you.');
    } catch (error) {
      console.error('Demo creation failed:', error);
    } finally {
      setLoading(false);
    }
  };

  // Register the addTour callback so TourCard can add tours when builder is open
  useEffect(() => {
    if (isOpen && registerAddTourCallback) {
      const unregister = registerAddTourCallback(addTour);
      return unregister;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // Only depend on isOpen - addTour and registerAddTourCallback are memoized and stable

  // Memoize generateItinerary to prevent recreation on every render
  const generateItineraryMemo = useCallback(async (tours: Tour[], currency: string = 'USD') => {
    if (tours.length === 0) {
      setItinerary(null);
      return;
    }

    try {
      setLoading(true);
      const generated = await customSafariService.generateItinerary(
        tours,
        safariName || undefined,
        currency // Use selected currency
      );

      // Link accommodation selections to tours in itinerary
      const { accommodationService } = await import('../../services/accommodationService');
      const updatedTours = await Promise.all(generated.tours.map(async (selectedTour: SelectedTour) => {
        const selection = accommodationSelections.find(s => s.tourId === selectedTour.tour.id);
        if (selection && selection.providerId) {
          try {
            const providers = await accommodationService.getProvidersByTour(selectedTour.tour.id);
            const provider = providers.find(p => p.id === selection.providerId);
            if (provider) {
              // Calculate nights from tour duration
              const durationMatch = selectedTour.tour.duration.match(/(\d+)/);
              const nights = durationMatch ? parseInt(durationMatch[1]) : selection.nights || 1;

              // Calculate distance (simplified - use 0 if not available)
              const distance = 0; // Can be enhanced with actual distance calculation

              return {
                ...selectedTour,
                selectedAccommodation: {
                  id: provider.id,
                  name: provider.name,
                  type: provider.type,
                  pricePerNight: provider.basePricePerNight || 0,
                  currency: provider.currency || 'USD',
                  nights: selection.nights || nights,
                  distance
                }
              };
            }
          } catch (error) {
            console.error('Error loading accommodation provider:', error);
          }
        }
        return selectedTour;
      }));

      // Recalculate pricing with accommodations
      const updatedPricing = await customSafariService.calculatePricing(
        updatedTours,
        generated.transfers,
        [],
        currency
      );

      generated.tours = updatedTours;
      generated.totalPrice = updatedPricing;

      setItinerary(generated);
    } catch (error) {
      console.error('Error generating itinerary:', error);
      setItinerary(null);
    } finally {
      setLoading(false);
    }
  }, [safariName, accommodationSelections]);

  useEffect(() => {
    if (selectedTours.length > 0) {
      generateItineraryMemo(selectedTours, selectedCurrency);
    } else {
      setItinerary(null);
    }
  }, [selectedTours, selectedCurrency, generateItineraryMemo, accommodationSelections]);

  // Handle currency change - recalculate pricing
  const handleCurrencyChange = async (newCurrency: string) => {
    setSelectedCurrency(newCurrency);
    if (itinerary && selectedTours.length > 0) {
      try {
        setLoading(true);
        // Recalculate pricing with new currency using existing transfers
        const newPricing = await customSafariService.calculatePricing(
          itinerary.tours,
          itinerary.transfers,
          [],
          newCurrency
        );
        setItinerary({
          ...itinerary,
          totalPrice: newPricing,
        });
      } catch (error) {
        console.error('Error updating currency:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const loadTours = async () => {
    try {
      setLoading(true);
      const tours = await tourService.getActiveTours();
      setAllTours(tours);
    } catch (error) {
      console.error('Error loading tours:', error);
    } finally {
      setLoading(false);
    }
  };

  // generateItinerary is now memoized as generateItineraryMemo above

  // addTour is now defined above using useCallback

  const removeTour = (tourId: string) => {
    setSelectedTours(selectedTours.filter(t => t.id !== tourId));
  };

  const handleSchedule = () => {
    setShowSchedulingModal(true);
  };

  const handleScheduleConfirm = async (startDate: string) => {
    if (itinerary) {
      const updated = await customSafariService.updateStartDate(itinerary, startDate);
      setItinerary(updated);
      setShowSchedulingModal(false);
      showSuccess('Safari Scheduled', `Start date set to ${new Date(startDate).toLocaleDateString()}`);
    }
  };

  const handleSaveToCart = async () => {
    if (!itinerary) return;

    // Check if user is authenticated
    if (!currentUser) {
      setShowAuthModal(true);
      return;
    }

    try {
      setLoading(true);
      await customSafariService.saveToCart(itinerary, currentUser.id);
      // Clear cache and trigger cart refresh event
      customSafariService.clearCartCache(currentUser.id);
      window.dispatchEvent(new Event('cartUpdated'));
      onClose();
      // Open cart to show the saved item
      setTimeout(() => openCart(), 100);
    } catch (error) {
      console.error('Error saving to cart:', error);
      showError('Failed to save to cart. Please try again.', 'Cart Error');
    } finally {
      setLoading(false);
    }
  };

  const handleAuthSuccess = async (user: User) => {
    await refreshUser(); // Refresh auth context
    setShowAuthModal(false);
    // Load tours after authentication
    await loadTours();
    // Retry saving to cart if itinerary exists
    if (itinerary) {
      await handleSaveToCart();
    }
  };

  const filteredTours = allTours.filter(tour =>
    tour.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tour.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tour.location.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-3 sm:p-4 md:p-8" style={{ pointerEvents: 'auto' }}>
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-xl transition-opacity"
        onClick={onClose}
        style={{ pointerEvents: 'auto' }}
      />

      {/* Main Panel */}
      <div className={`relative w-full h-[92vh] sm:h-auto max-h-[96vh] sm:max-h-[90vh] sm:max-w-5xl ${actualTheme === 'dark' ? 'bg-slate-900' : 'bg-white'
        } rounded-[2rem] sm:rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300 flex flex-col`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b ${actualTheme === 'dark' ? 'border-slate-700' : 'border-neutral-200'
          }`}>
          <div className="flex-1">
            <h2 className={`text-2xl font-bold ${actualTheme === 'dark' ? 'text-white' : 'text-primary-900'
              }`}>
              Custom Safari Builder
            </h2>
            <div className="flex items-center gap-3 mt-1">
              <p className={`text-sm ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                }`}>
                Select multiple tours to create your perfect safari experience
              </p>
              {currentUser ? (
                <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs ${actualTheme === 'dark' ? 'bg-green-900/30 text-green-300' : 'bg-green-50 text-green-700'
                  }`}>
                  <UserIcon className="h-3 w-3" />
                  <span>{currentUser.name}</span>
                </div>
              ) : (
                <button
                  onClick={() => setShowAuthModal(true)}
                  className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium transition-all ${actualTheme === 'dark'
                    ? 'bg-primary-700 hover:bg-primary-600 text-white'
                    : 'bg-primary-800 hover:bg-primary-700 text-white'
                    }`}
                >
                  <UserIcon className="h-3 w-3" />
                  Sign In to Save
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleCreateDemo}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${actualTheme === 'dark'
                ? 'bg-blue-700 hover:bg-blue-600 text-white shadow-lg shadow-blue-900/20'
                : 'bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-500/20'
                }`}
            >
              <SparklesIcon className="h-4 w-4" />
              Try 7-Day Demo
            </button>
            <button
              onClick={onClose}
              className={`p-2 rounded-lg transition-colors ${actualTheme === 'dark'
                ? 'text-slate-400 hover:bg-slate-800 hover:text-white'
                : 'text-neutral-600 hover:bg-neutral-100 hover:text-primary-800'
                }`}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className={`flex border-b ${actualTheme === 'dark' ? 'border-slate-700' : 'border-neutral-200'
          }`}>
          <button
            onClick={() => setActiveTab('select')}
            className={`flex-1 px-6 py-4 font-medium transition-colors ${activeTab === 'select'
              ? actualTheme === 'dark'
                ? 'text-primary-400 border-b-2 border-primary-400'
                : 'text-primary-800 border-b-2 border-primary-800'
              : actualTheme === 'dark'
                ? 'text-slate-400 hover:text-slate-300'
                : 'text-neutral-600 hover:text-primary-700'
              }`}
          >
            Select Tours ({selectedTours.length})
          </button>
          <button
            onClick={() => setActiveTab('itinerary')}
            disabled={selectedTours.length === 0}
            className={`flex-1 px-6 py-4 font-medium transition-colors ${selectedTours.length === 0
              ? 'opacity-50 cursor-not-allowed'
              : activeTab === 'itinerary'
                ? actualTheme === 'dark'
                  ? 'text-primary-400 border-b-2 border-primary-400'
                  : 'text-primary-800 border-b-2 border-primary-800'
                : actualTheme === 'dark'
                  ? 'text-slate-400 hover:text-slate-300'
                  : 'text-neutral-600 hover:text-primary-700'
              }`}
          >
            Itinerary & Pricing
          </button>
          <button
            onClick={() => setActiveTab('accommodations')}
            disabled={selectedTours.length === 0}
            className={`flex-1 px-6 py-4 font-medium transition-colors ${selectedTours.length === 0
              ? 'opacity-50 cursor-not-allowed'
              : activeTab === 'accommodations'
                ? actualTheme === 'dark'
                  ? 'text-primary-400 border-b-2 border-primary-400'
                  : 'text-primary-800 border-b-2 border-primary-800'
                : actualTheme === 'dark'
                  ? 'text-slate-400 hover:text-slate-300'
                  : 'text-neutral-600 hover:text-primary-700'
              }`}
          >
            Accommodations {accommodationSelections.length > 0 && `(${accommodationSelections.length})`}
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {activeTab === 'select' ? (
            <div className="p-6">
              {/* Search Bar */}
              <div className="mb-6">
                <input
                  type="text"
                  placeholder="Search tours by name, category, or location..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`w-full px-4 py-3 rounded-lg border ${actualTheme === 'dark'
                    ? 'bg-slate-800 border-slate-700 text-white placeholder-slate-400'
                    : 'bg-white border-neutral-300 text-neutral-900 placeholder-neutral-500'
                    } focus:outline-none focus:ring-2 focus:ring-primary-500`}
                />
              </div>

              {/* Selected Tours Summary */}
              {selectedTours.length > 0 && (
                <div className={`mb-6 p-4 rounded-lg ${actualTheme === 'dark' ? 'bg-slate-800' : 'bg-primary-50'
                  }`}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className={`font-semibold ${actualTheme === 'dark' ? 'text-white' : 'text-primary-900'
                      }`}>
                      Selected Tours ({selectedTours.length})
                    </h3>
                    <button
                      onClick={() => setSelectedTours([])}
                      className={`text-sm ${actualTheme === 'dark'
                        ? 'text-slate-400 hover:text-red-400'
                        : 'text-neutral-600 hover:text-red-600'
                        }`}
                    >
                      Clear All
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedTours.map((tour) => (
                      <div
                        key={tour.id}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${actualTheme === 'dark'
                          ? 'bg-slate-700 text-white'
                          : 'bg-white text-primary-900 border border-primary-200'
                          }`}
                      >
                        <span>{tour.title}</span>
                        <button
                          onClick={() => removeTour(tour.id)}
                          className="hover:text-red-500"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tours Grid */}
              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-800"></div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {filteredTours.map((tour) => {
                    const isSelected = selectedTours.some(t => t.id === tour.id);
                    return (
                      <div key={tour.id} className="relative">
                        <TourCard tour={tour} showQuickBook={false} />
                        <div className="absolute top-4 right-4">
                          {isSelected ? (
                            <button
                              disabled
                              className={`p-2 rounded-full ${actualTheme === 'dark'
                                ? 'bg-primary-600 text-white'
                                : 'bg-primary-800 text-white'
                                } cursor-not-allowed`}
                            >
                              <CheckIcon className="h-5 w-5" />
                            </button>
                          ) : (
                            <button
                              onClick={() => addTour(tour)}
                              className={`p-2 rounded-full transition-all ${actualTheme === 'dark'
                                ? 'bg-slate-800/90 hover:bg-primary-600 text-white'
                                : 'bg-white/90 hover:bg-primary-800 text-primary-800 hover:text-white'
                                } shadow-lg hover:scale-110`}
                            >
                              <PlusIcon className="h-5 w-5" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {filteredTours.length === 0 && !loading && (
                <div className="text-center py-16 px-4">
                  <div className={`text-6xl mb-6 opacity-20`}>🔍</div>
                  <h3 className={`text-xl font-bold mb-2 ${actualTheme === 'dark' ? 'text-white' : 'text-primary-900'}`}>
                    No tours found
                  </h3>
                  <p className={`text-sm mb-8 max-w-sm mx-auto ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'}`}>
                    Try searching for something else, or start with our pre-built demo safari to see how it works.
                  </p>
                  <button
                    onClick={handleCreateDemo}
                    className="inline-flex items-center gap-2 px-8 py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl transition-all shadow-xl shadow-blue-500/30 hover:scale-105 active:scale-95"
                  >
                    <SparklesIcon className="h-5 w-5" />
                    Launch 7-Day Kenya Demo
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="p-6">
              {itinerary ? (
                <div className="space-y-6">
                  {/* Safari Name Input */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
                      }`}>
                      Name Your Custom Safari
                    </label>
                    <input
                      type="text"
                      value={safariName}
                      onChange={(e) => {
                        setSafariName(e.target.value);
                        if (itinerary) {
                          const updated = { ...itinerary, name: e.target.value };
                          setItinerary(updated);
                        }
                      }}
                      placeholder="e.g., My Dream Kenya Safari"
                      className={`w-full px-4 py-2 rounded-lg border ${actualTheme === 'dark'
                        ? 'bg-slate-800 border-slate-700 text-white'
                        : 'bg-white border-neutral-300 text-neutral-900'
                        } focus:outline-none focus:ring-2 focus:ring-primary-500`}
                    />
                  </div>

                  {/* Itinerary View */}
                  <ItineraryView
                    itinerary={itinerary}
                    onItineraryUpdate={setItinerary}
                    onLaunchDemo={handleCreateDemo}
                  />

                  {/* Pricing Breakdown */}
                  <PricingBreakdown
                    pricing={itinerary.totalPrice}
                    selectedCurrency={selectedCurrency}
                    onCurrencyChange={handleCurrencyChange}
                  />
                </div>
              ) : (
                <div className={`text-center py-12 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                  }`}>
                  <p>Select tours to generate your itinerary</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'accommodations' && (
            <div className="p-6">
              {selectedTours.length === 0 ? (
                <div className={`text-center py-12 ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                  }`}>
                  <p>Select tours first to choose accommodations</p>
                </div>
              ) : (
                <AccommodationSelector
                  tours={selectedTours}
                  selections={accommodationSelections}
                  onSelectionsChange={setAccommodationSelections}
                  currency={selectedCurrency}
                />
              )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        {selectedTours.length > 0 && (
          <div className={`p-4 md:p-6 border-t ${actualTheme === 'dark' ? 'border-slate-700 bg-slate-800' : 'border-neutral-200 bg-neutral-50'
            }`}>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex-1">
                <p className={`text-sm ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
                  }`}>
                  {selectedTours.length} tour{selectedTours.length !== 1 ? 's' : ''} selected
                </p>
                {itinerary && (
                  <p className={`text-base md:text-lg font-bold mt-1 ${actualTheme === 'dark' ? 'text-white' : 'text-primary-900'
                    }`}>
                    Total: {itinerary.totalPrice.currency} {itinerary.totalPrice.total.toLocaleString()}
                  </p>
                )}
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
                <button
                  onClick={handleSaveToCart}
                  className={`flex items-center justify-center gap-2 px-4 md:px-6 py-2 md:py-3 rounded-lg font-medium transition-all ${actualTheme === 'dark'
                    ? 'bg-slate-700 hover:bg-slate-600 text-white'
                    : 'bg-white border-2 border-primary-800 text-primary-800 hover:bg-primary-50'
                    }`}
                >
                  <ShoppingCartIcon className="h-5 w-5" />
                  <span className="text-sm md:text-base">Save to Cart</span>
                </button>
                <button
                  onClick={handleSchedule}
                  className={`flex items-center justify-center gap-2 px-4 md:px-6 py-2 md:py-3 rounded-lg font-medium transition-all ${actualTheme === 'dark'
                    ? 'bg-primary-700 hover:bg-primary-600 text-white'
                    : 'bg-primary-800 hover:bg-primary-700 text-white'
                    }`}
                >
                  <CalendarIcon className="h-5 w-5" />
                  <span className="text-sm md:text-base">Schedule</span>
                </button>
                <button
                  onClick={() => {
                    if (!currentUser || !isAuthenticated) {
                      setShowAuthModal(true);
                      return;
                    }
                    if (!itinerary) {
                      showWarning('Please generate an itinerary first', 'Missing Itinerary');
                      return;
                    }
                    setShowBookingModal(true);
                  }}
                  className={`px-6 py-3 rounded-lg font-medium transition-all ${actualTheme === 'dark'
                    ? 'bg-primary-800 hover:bg-primary-700 text-white'
                    : 'bg-primary-800 hover:bg-primary-700 text-white'
                    }`}
                >
                  Book Now
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* User Authentication Modal */}
      <UserAuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
        mode="signin"
      />

      {/* Booking Modal */}
      {itinerary && (
        <BookingModal
          isOpen={showBookingModal}
          onClose={() => setShowBookingModal(false)}
          itinerary={itinerary}
          accommodationSelections={accommodationSelections}
          onBookingSuccess={() => {
            // Refresh cart and close builder
            window.dispatchEvent(new Event('cartUpdated'));
            onClose();
          }}
        />
      )}
      <SchedulingModal
        isOpen={showSchedulingModal}
        onClose={() => setShowSchedulingModal(false)}
        onConfirm={handleScheduleConfirm}
        currentDate={itinerary?.startDate}
        minDate={
          itinerary?.tours
            .filter(st => st.tour.status === 'scheduled' && st.tour.safariStartDate)
            .map(st => st.tour.safariStartDate!)
            .reduce((latest, date) => date > latest ? date : latest, new Date().toISOString().split('T')[0])
            .split('T')[0]
        }
        maxDate={
          itinerary?.tours
            .filter(st => st.tour.status === 'scheduled' && st.tour.safariEndDate)
            .map(st => st.tour.safariEndDate!)
            .reduce((earliest, date) => date < earliest ? date : earliest, '9999-12-31')
            .split('T')[0] === '9999-12-31' ? undefined : itinerary?.tours
              .filter(st => st.tour.status === 'scheduled' && st.tour.safariEndDate)
              .map(st => st.tour.safariEndDate!)
              .reduce((earliest, date) => date < earliest ? date : earliest, '9999-12-31')
              .split('T')[0]
        }
      />
    </div>
  );
};

export default CustomSafariBuilder;

